/**
 * Batch Add Component
 * Form to create a new batch
 */

import { Component, OnInit, OnDestroy, signal, inject, ChangeDetectionStrategy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { MessageService } from 'primeng/api';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';

import { AddFormComponent, FormField } from '@shared/components/add-form/add-form.component';
import { BatchService } from '../services/batch.service';
import { BatchCreateRequest } from '../models/batch.interface';
import { ErrorTranslatorService } from '@app/shared/services/error-translator-service';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorDialogService } from '@app/shared/services/error-dialog.service';

@Component({
  selector: 'app-batch-add',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ToastModule,
    TranslateModule,
    AddFormComponent,
    SelectModule,
    InputTextModule,
  ],
  providers: [MessageService],
  templateUrl: './batch-add.component.html',
  styleUrl: './batch-add.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BatchAddComponent implements OnInit, OnDestroy {
  @ViewChild('addForm') addFormComponent!: AddFormComponent;

  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);
  private readonly translateService = inject(TranslateService);
  private readonly batchService = inject(BatchService);
  private readonly destroy$ = new Subject<void>();
  private readonly errorTranslatorService = inject(ErrorTranslatorService);
  private readonly errorDialogService = inject(ErrorDialogService);

  // Form configuration
  formFields = signal<FormField[]>([]);
  loading = signal(false);
  private skipCanDeactivate = false;

  // Form data
  formData: any = {
    batchId: '',
    skuId: null,
    expiryDate: null,
  };

  // SKU options for dropdown
  skuOptions = signal<Array<{ label: string; value: number; code: string; name: string }>>([]);
  loadingOptions = signal(false);

  ngOnInit(): void {
    this.loadSkuOptions();
    this.initializeFormFields();
  }

  private loadSkuOptions(): void {
    // Load all SKU options when component initializes
    this.loadingOptions.set(true);
    this.batchService
      .getAllSkuOptions()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (skus) => {
          const options = skus.map((sku) => ({
            label: sku.name ? `${sku.code} - ${sku.name}` : sku.code,
            value: sku.id,
            code: sku.code,
            name: sku.name,
          }));
          this.skuOptions.set(options);
          this.updateFormFields();
          this.loadingOptions.set(false);
        },
        error: (error) => {
          console.error('Error loading SKU options:', error);
          this.skuOptions.set([]);
          this.updateFormFields();
          this.loadingOptions.set(false);
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onFormAction(action: string): void {
    if (action === 'cancel') {
      this.router.navigate(['/stock/batch']);
    } else if (action === 'save') {
      this.onSave();
    }
  }

  onFormValueChanges(changes: any): void {
    // Update form data
    Object.assign(this.formData, changes);
  }


  private onSave(): void {
    // Validate required fields
    if (!this.formData.batchId || !this.formData.skuId) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant('batch_management.messages.required_fields'),
      });
      return;
    }

    // Find SKU code from selected SKU ID
    const selectedSku = this.skuOptions().find((sku) => sku.value === this.formData.skuId);

    if (!selectedSku || !selectedSku.code) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant('batch_management.messages.sku_not_found'),
      });
      return;
    }

    this.loading.set(true);

    const request: BatchCreateRequest = {
      batchId: this.formData.batchId,
      skuCode: selectedSku.code,
      expiryDate: this.toLocalDateString(this.formData.expiryDate),
    };

    this.batchService
      .createBatch(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.loading.set(false);
          this.skipCanDeactivate = true;
          this.addFormComponent?.form?.markAsPristine();
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant('batch_management.messages.created_successfully'),
          });
          setTimeout(() => {
            this.router.navigate(['/stock/batch']);
          }, 1000);
        },
        error: (error: HttpErrorResponse) => {
          this.errorDialogService.showError(this.errorTranslatorService.translate(error.error.message))
          this.loading.set(false);
        },
      });
  }

  canDeactivate(): boolean {
    if (this.skipCanDeactivate || this.loading()) {
      return true;
    }

    return !(this.addFormComponent?.form?.dirty ?? false);
  }


  private initializeFormFields(): void {
    this.updateFormFields();
  }

  /** Converts the date-picker's Date value to a LocalDate-compatible 'yyyy-MM-dd' string. */
  private toLocalDateString(value: unknown): string | null {
    if (!value) {
      return null;
    }
    const date = value instanceof Date ? value : new Date(value as string);
    if (isNaN(date.getTime())) {
      return null;
    }
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }

  private updateFormFields(): void {
    const fields: FormField[] = [
      {
        controlName: 'batchId',
        label: 'batch_management.form.fields.batch_id',
        type: 'text',
        required: true,
        placeholder: 'batch_management.form.placeholders.enter_batch_id',
        section: 'batch_management.form.sections.basic_information',
        maxLength: 255,
      },
      {
        controlName: 'skuId',
        label: 'batch_management.form.fields.sku_name',
        type: 'searchableDropdown',
        required: true,
        placeholder: 'batch_management.form.placeholders.select_sku',
        options: this.skuOptions(),
        section: 'batch_management.form.sections.basic_information',
      },
      {
        controlName: 'expiryDate',
        label: 'batch_management.form.fields.expiry_date',
        type: 'date',
        required: false,
        placeholder: 'batch_management.form.placeholders.enter_expiry_date',
        section: 'batch_management.form.sections.basic_information',
      },
    ];

    this.formFields.set(fields);
  }
}

